import { ShopAvailabilityProvider } from "./components/shopAvailability";
// App.jsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { shopTheme } from "./functions/shopTheme";
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";

// Pages
import Login from "./pages/login";
import RecoverPassword from "./pages/login/recoverPassword";
import { VerifyPhone } from "./pages/login/verifyPhone";

import { Products } from "./pages/products";
import { Product } from "./pages/product";
import { Categories } from "./pages/categories";

import { Profile } from "./pages/profile";
import { Details } from "./pages/profile/pages/details";
import { History } from "./pages/profile/pages/history";
import { Policies } from "./pages/profile/pages/policies";
import { OrderTraking } from "./pages/profile/pages/history/orderTraking";
import { DeliveryOptions } from "./pages/profile/pages/delivery-options";
import { InvoiceOptions } from "./pages/profile/pages/invoice-options";

import { Cart } from "./pages/cart";
import { Checkout } from "./pages/checkout";
import { ResponsePage } from "./pages/responsePage";

import { ProtectedRoute } from "./components/security/protectedRoute";
import { ShopInstallProvider } from "./components/shopInstall";
import { ProductModalProvider } from "./components/productModal";
import { CartDrawerProvider } from "./components/cartDrawer";
import { HeaderMobile } from "./components/headerMobile";
import { LoaderComponent } from "./components/loaderComponent";
import { Alert } from "./components/alert";
import Initializer from "./hooks/initializer";

import packageJson from "../package.json";
import "./custom.css";
import "./shop-design.css";
import "./responsive-design.css";

/* =========
   Layouts
   ========= */

// Header común + contenido
const HeaderLayout = () => {
  const location = useLocation();
  const checkoutAccess = location.pathname === "/login" && new URLSearchParams(location.search).get("returnTo") === "/checkout";
  return <>
    {!checkoutAccess && <HeaderMobile />}
    <div className="shopContainer">
      <Outlet />
    </div>
  </>;
};

// Contenedor con grilla + carrito (reutilizable)
const ShopContainer = ({ children }) => <>{children}</>;

// Layout perfil (sidebar perfil + contenido)
const ProfileLayout = () => {
  const { userConfig, corporationData } = useSelector(state => state.aplicationConfig);
  const identity = JSON.stringify([userConfig.userData?.customerId, userConfig.token, corporationData.info?.corporation?.corporationId, corporationData.info?.corporationId]);
  return <>
    <HeaderMobile account />
    <div className="profile" key={identity}>
      <Profile />
      <Outlet />
    </div>
  </>;
};

// Wrapper de protección con Outlet
const Protected = ({ page }) => (
  <ProtectedRoute page={page}>
    <Outlet />
  </ProtectedRoute>
);

const Navigation = () => {
  const [bootstrap, setBootstrap] = useState("loading");
  const [fontReady, setFontReady] = useState(false);
  useEffect(() => {
    let active = true;
    const font = document.fonts?.load('400 16px "Inter"') || Promise.resolve();
    font.catch(() => {}).then(() => { if (active) setFontReady(true); });
    return () => { active = false; };
  }, []);
  const ready = bootstrap === "ready" && fontReady;
  return (
    <Router>
      <CartDrawerProvider>
      <ProductModalProvider>
      <Initializer onBootstrap={setBootstrap} />
      {!ready && <main className="eats-bootstrap" aria-busy={bootstrap !== "error"}>
        {bootstrap === "error" ? <div role="alert"><p>No se pudo cargar la tienda.</p><button onClick={() => window.location.reload()}>Volver a intentar</button></div> : <p role="status">Cargando tienda…</p>}
      </main>}
      {ready && <>
      <LoaderComponent />
      <Alert />

      <Routes>
        {/* Rutas con header genérico */}
        <Route element={<HeaderLayout />}>
          <Route
            path="/"
            element={
              <ShopContainer>
                <Products />
              </ShopContainer>
            }
          />

          <Route path="/login" element={<Login />} />
          <Route path="/change-password/:token" element={<RecoverPassword />} />
          <Route path="/verify-phone" element={<VerifyPhone />} />
          <Route path="/categories" element={<Categories />} />

          <Route
            path="/products/:category?"
            element={
              <ShopContainer>
                <Products />
              </ShopContainer>
            }
          />

          <Route
            path="/tienda/:urlSlug?"
            element={
              <ShopContainer>
                <Product />
              </ShopContainer>
            }
          />

          <Route path="/carrito" element={<Cart />} />
        </Route>

        {/* Checkout protegido (con header) */}
        <Route element={<Protected page="checkout" />}>
          <Route element={<HeaderLayout />}>
            <Route path="/checkout" element={<Checkout />} />
          </Route>
        </Route>

        {/* Página de respuesta protegida (sin header) */}
        <Route element={<Protected page="checkoutPage" />}>
          <Route path="/checkoutPage" element={<ResponsePage />} />
        </Route>

        {/* Zona de perfil protegida (mismo header + layout de perfil) */}
        <Route element={<Protected page="profile" />}>
          <Route element={<ProfileLayout />}>
            <Route path="/profile" element={<Navigate to="/details" replace />} />
            <Route path="/details" element={<Details />} />
            <Route path="/history" element={<History />} />
            <Route path="/order-tracking/:id?" element={<OrderTraking />} />
            <Route path="/policies" element={<Policies />} />
            <Route path="/delivery-options" element={<DeliveryOptions />} />
            <Route path="/invoice-options" element={<InvoiceOptions />} />
          </Route>
        </Route>

        {/* Fallback */}
        <Route element={<HeaderLayout />}>
          <Route
            path="*"
            element={
              <ShopContainer>
                <Products />
              </ShopContainer>
            }
          />
        </Route>
      </Routes>
      </>}
      </ProductModalProvider>
      </CartDrawerProvider>
    </Router>
  );
};

function App() {
  const color = useSelector(state => state.aplicationConfig.corporationData.info?.corporation?.color);
  return (
    <div className="shop-app" style={shopTheme(color)}>
      <div className="appVersion">
        {packageJson.version + "/" + packageJson.dependencies["@delibrandev/utils"].replace("^", "")}
      </div>
      <ShopAvailabilityProvider><ShopInstallProvider><Navigation /></ShopInstallProvider></ShopAvailabilityProvider>
    </div>
  );
}

export default App;
